import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Eye, EyeOff, Briefcase, Loader2, User, Building2, GraduationCap, Search, Calendar, Globe, CheckCircle2, Mail } from 'lucide-react';
import axios from 'axios';
import UniversityOnboardingWizard from './UniversityOnboardingWizard';
import CourseProviderOnboardingWizard from './CourseProviderOnboardingWizard';
import { detectCountry } from '../lib/localCurrency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Arithmetic CAPTCHA helpers ─────────────────────────────────────────────
function generateCaptcha(difficulty = 'medium') {
  const max = difficulty === 'easy' ? 5 : difficulty === 'hard' ? 20 : 10;
  const a = Math.floor(Math.random() * max) + 1;
  const b = Math.floor(Math.random() * max) + 1;
  const ops = difficulty === 'easy' ? ['+'] : ['+', '-', '×'];
  const op = ops[Math.floor(Math.random() * ops.length)];
  let answer;
  if (op === '+') answer = a + b;
  else if (op === '-') answer = a - b;
  else answer = a * b;
  return { question: `What is ${a} ${op} ${b}?`, answer };
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register,  branding } = useAuth();

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    user_type: searchParams.get('type') || 'job_seeker',
    country: ''
  });
  const [countries, setCountries] = useState([]);

  // Prefer the visitor's detected country, then fall back to the platform default.
  useEffect(() => {
    axios.get(`${API_URL}/api/countries`)
      .then(res => {
        const list = res.data.countries || [];
        setCountries(list);
        const detected = list.find(c => c.code?.toUpperCase() === detectCountry());
        const def = detected || list.find(c => c.is_default) || list[0];
        if (def) setFormData(prev => (prev.country ? prev : { ...prev, country: def.code }));
      })
      .catch(() => setCountries([]));
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // CAPTCHA config from backend
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: true, difficulty: 'medium' });
  const [captchaConfigLoaded, setCaptchaConfigLoaded] = useState(false);

  // CAPTCHA state
  const [captcha, setCaptcha] = useState(() => generateCaptcha('medium'));
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  // Fetch CAPTCHA config on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/settings/captcha`)
      .then(res => {
        setCaptchaConfig(res.data);
        setCaptcha(generateCaptcha(res.data.difficulty || 'medium'));
      })
      .catch(() => { /* keep defaults */ })
      .finally(() => setCaptchaConfigLoaded(true));
  }, []);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha(captchaConfig.difficulty || 'medium'));
    setCaptchaInput('');
    setCaptchaError('');
  }, [captchaConfig.difficulty]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!validateEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!formData.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Validate CAPTCHA only if enabled
    if (captchaConfig.enabled) {
      const userAnswer = parseInt(captchaInput.trim(), 10);
      if (isNaN(userAnswer) || userAnswer !== captcha.answer) {
        setCaptchaError('Incorrect answer. Please try again.');
        refreshCaptcha();
        return;
      }
    }

    setLoading(true);
    try {
      await register(formData);
      setRegisteredEmail(formData.email);
      setRegistered(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    const { user_type } = formData;
    if (user_type === 'employer') navigate('/employer/dashboard');
    else if (user_type === 'university_manager') navigate('/university/dashboard');
    else if (user_type === 'course_provider') navigate('/course-provider/dashboard');
    else if (user_type === 'event_host') navigate('/event-host/dashboard');
    else if (user_type === 'recruiter') navigate('/recruiter-dashboard');
    else navigate('/profile');
  };

  // ── Post-registration: role-specific onboarding wizard ─────────────────
  if (registered && formData.user_type === 'university_manager') {
    return <UniversityOnboardingWizard />;
  }
  if (registered && formData.user_type === 'course_provider') {
    return <CourseProviderOnboardingWizard />;
  }

  // ── Post-registration: email verification prompt ──────────────────────
  if (registered) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            {/* <div className="w-12 h-12 rounded-xl bg-[#003366] flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-[#003366]">WorksRun</span> */}
            {branding?.logo_url ? (
                          <img
                            src={
                              branding.logo_url.startsWith('http')
                                ? branding.logo_url
                                : `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
                            }
                            alt={branding.platform_name || 'Logo'}
                            className="h-10 w-auto object-contain"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-[#003366] flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-white" />
                          </div>
                        )}
          </Link>

          <Card className="shadow-lg text-center">
            <CardContent className="p-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
              <p className="text-muted-foreground mb-4">
                Welcome to WorksRun. We've sent a verification email to:
              </p>
              <p className="font-semibold text-[#003366] bg-[#003366]/5 rounded-lg px-4 py-2 mb-6 break-all">
                {registeredEmail}
              </p>
              <div className="space-y-2 text-sm text-left bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-amber-800">Check your inbox (and spam folder) for the verification link.</span>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-amber-800">Click the link to activate your account. The link expires in 24 hours.</span>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-amber-800">You can still use the platform while unverified, but some features require verification.</span>
                </div>
              </div>
              <Button
                onClick={handleContinue}
                className="w-full bg-[#003366] hover:bg-[#003366]/90 h-11"
              >
                Continue to Dashboard
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Didn't receive it? Check spam or use the <strong>Resend email</strong> link shown in the banner after you log in.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Registration form ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          {/* <div className="w-12 h-12 rounded-xl bg-[#003366] flex items-center justify-center">
            <Briefcase className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-2xl text-[#003366]">WorksRun</span> */}

          {branding?.logo_url ? (
              <img
                src={
                  branding.logo_url.startsWith('http')
                    ? branding.logo_url
                    : `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
                }
                alt={branding.platform_name || 'Logo'}
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-[#003366] flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
            )}
        </Link>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Create Your Account</CardTitle>
            <CardDescription>Join thousands of professionals on WorksRun</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Account Type Selection */}
              <div className="space-y-3">
                <Label>I want to</Label>
                <RadioGroup
                  value={formData.user_type}
                  onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                  className="grid grid-cols-2 gap-3"
                >
                  <div>
                    <RadioGroupItem value="job_seeker" id="job_seeker" className="peer sr-only" />
                    <Label
                      htmlFor="job_seeker"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-[#003366] peer-data-[state=checked]:bg-[#003366]/5 cursor-pointer transition-all text-center"
                      data-testid="select-job-seeker"
                    >
                      <Briefcase className="w-5 h-5 mb-1 text-[#003366]" />
                      <span className="font-medium text-sm">Find Jobs</span>
                    </Label>
                  </div>

                  <div>
                    <RadioGroupItem value="employer" id="employer" className="peer sr-only" />
                    <Label
                      htmlFor="employer"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-[#003366] peer-data-[state=checked]:bg-[#003366]/5 cursor-pointer transition-all text-center"
                      data-testid="select-employer"
                    >
                      <User className="w-5 h-5 mb-1 text-[#003366]" />
                      <span className="font-medium text-sm">Hire Talent</span>
                    </Label>
                  </div>

                  <div>
                    <RadioGroupItem value="university_manager" id="university_manager" className="peer sr-only" />
                    <Label
                      htmlFor="university_manager"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-[#003366] peer-data-[state=checked]:bg-[#003366]/5 cursor-pointer transition-all text-center"
                    >
                      <GraduationCap className="w-5 h-5 mb-1 text-[#003366]" />
                      <span className="font-medium text-sm">Post Courses</span>
                      <span className="text-xs text-muted-foreground mt-0.5 leading-tight">For universities</span>
                    </Label>
                  </div>

                  <div>
                    <RadioGroupItem value="course_provider" id="course_provider" className="peer sr-only" />
                    <Label
                      htmlFor="course_provider"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-[#003366] peer-data-[state=checked]:bg-[#003366]/5 cursor-pointer transition-all text-center"
                      data-testid="select-course-provider"
                    >
                      <GraduationCap className="w-5 h-5 mb-1 text-[#003366]" />
                      <span className="font-medium text-sm">Offer Training</span>
                      <span className="text-xs text-muted-foreground mt-0.5 leading-tight">Courses &amp; trainers</span>
                    </Label>
                  </div>

                  <div>
                    <RadioGroupItem value="recruiter" id="recruiter" className="peer sr-only" />
                    <Label
                      htmlFor="recruiter"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-[#003366] peer-data-[state=checked]:bg-[#003366]/5 cursor-pointer transition-all text-center"
                    >
                      <Search className="w-5 h-5 mb-1 text-[#003366]" />
                      <span className="font-medium text-sm">Recruit Staff</span>
                      <span className="text-xs text-muted-foreground mt-0.5 leading-tight">Executive recruitment</span>
                    </Label>
                  </div>

                  <div>
                    <RadioGroupItem value="event_host" id="event_host" className="peer sr-only" />
                    <Label
                      htmlFor="event_host"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-[#003366] peer-data-[state=checked]:bg-[#003366]/5 cursor-pointer transition-all text-center"
                    >
                      <Calendar className="w-5 h-5 mb-1 text-[#003366]" />
                      <span className="font-medium text-sm">Host Events</span>
                    </Label>
                  </div>

                  <div>
                    <RadioGroupItem value="student" id="student" className="peer sr-only" />
                    <Label
                      htmlFor="student"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-[#003366] peer-data-[state=checked]:bg-[#003366]/5 cursor-pointer transition-all text-center"
                    >
                      <Globe className="w-5 h-5 mb-1 text-[#003366]" />
                      <span className="font-medium text-sm">Study Abroad</span>
                      <span className="text-xs text-muted-foreground mt-0.5 leading-tight">International students</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  data-testid="register-name"
                />
              </div>

              {countries.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="register-country"
                  >
                    {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
              </div>

              {/* CAPTCHA — only shown when enabled by admin */}
              {captchaConfig.enabled && (
                <div className="space-y-2">
                  <Label htmlFor="captcha">Security Check</Label>
                  <div className="flex items-center gap-3 bg-gray-50 border rounded-lg px-4 py-3">
                    <span className="font-mono font-semibold text-[#003366] text-base select-none">
                      {captcha.question}
                    </span>
                    <Input
                      id="captcha"
                      type="number"
                      placeholder="Answer"
                      value={captchaInput}
                      onChange={(e) => { setCaptchaInput(e.target.value); setCaptchaError(''); }}
                      className="w-24 text-center"
                      required={captchaConfig.enabled}
                      data-testid="register-captcha"
                    />
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      className="text-xs text-[#003366] hover:underline shrink-0"
                      title="Get a new question"
                    >
                      New question
                    </button>
                  </div>
                  {captchaError && (
                    <p className="text-xs text-red-600">{captchaError}</p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#E11D48] hover:bg-[#E11D48]/90 h-11"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Account
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="text-[#003366] hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[#003366] hover:underline">Privacy Policy</Link>
              </p>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-[#003366] font-medium hover:underline" data-testid="go-to-login">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
